import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label } from "../roads/_roadsCommon";

/** Ch 27 v2 — Physical MEP routing (HVAC ducts, plumbing pipes, cable trays, fire). */
export default function MEPRoutingPanel({ projectId }) {
  const [model, setModel] = useState(null);
  const [palette, setPalette] = useState(null);
  const [clashes, setClashes] = useState(null);
  const [tab, setTab] = useState("hvac");
  const [riserSvg, setRiserSvg] = useState("");
  const [err, setErr] = useState("");

  const reload = useCallback(async () => {
    try {
      const [m, p, c] = await Promise.all([
        api.get(`/projects/${projectId}/mep-re/v2/model`),
        api.get(`/projects/${projectId}/mep-re/v2/palette`),
        api.get(`/projects/${projectId}/mep-re/v2/clashes`),
      ]);
      setModel(m.data.model);
      setPalette(p.data);
      setClashes(c.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const addItem = async (defn) => {
    const idx = (model?.items?.length || 0);
    let geom_type = defn.geom; let points = [];
    if (geom_type === "point") points = [[5 + idx, 5]];
    else if (geom_type === "polyline") points = [[idx, 0], [idx + 10, 0]];
    else if (geom_type === "rect") points = [[idx, idx], [idx + (defn.default_w_m || 2), idx + (defn.default_h_m || 1)]];
    try {
      await api.post(`/projects/${projectId}/mep-re/v2/items`,
        { discipline: tab, type: defn.id, level: "GF", geom_type, points });
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const delItem = async (iid) => {
    await api.delete(`/projects/${projectId}/mep-re/v2/items/${iid}`);
    reload();
  };
  const loadRiser = async () => {
    const r = await api.get(`/projects/${projectId}/mep-re/v2/riser/${tab}`);
    setRiserSvg(r.data.svg);
  };

  const items = (model?.items || []).filter(it => it.discipline === tab);
  const list = palette?.[tab] || [];

  return (
    <div data-testid="mep-routing-v2" style={{ display: "grid",
            gridTemplateColumns: "260px 1fr 320px", gap: 12, padding: 16,
            height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* Palette */}
      <div style={{ ...card, padding: 12, overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
          {["hvac", "electrical", "plumbing", "fire"].map(d => (
            <button key={d} data-testid={`mepv2-tab-${d}`}
                style={{ ...btnGhost, padding: "4px 6px", fontSize: 10,
                            ...(tab === d ? { background: "#F26B1F", color: "#0e0e10" } : {}) }}
                onClick={() => setTab(d)}>{d.toUpperCase()}</button>
          ))}
        </div>
        <div style={label}>{tab.toUpperCase()} palette</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
          {list.map(d => (
            <button key={d.id} data-testid={`mepv2-add-${d.id}`}
                style={{ ...btnGhost, textAlign: "left", padding: "5px 8px", fontSize: 11 }}
                onClick={() => addItem(d)}>+ {d.label}</button>
          ))}
        </div>
      </div>

      {/* Items list */}
      <div style={{ display: "grid", gridTemplateRows: "1fr auto", gap: 12, minHeight: 0 }}>
        <div style={{ ...card, padding: 12, overflowY: "auto" }}>
          <div style={label}>{tab.toUpperCase()} placed items ({items.length})</div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 11 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
              {["Label", "Type", "Level", "Geom", ""].map(h =>
                <th key={h} style={{ textAlign: "left", padding: 4, color: "var(--pf-muted)" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} data-testid={`mepv2-item-${it.id}`}>
                  <td style={{ padding: 4 }}>{it.label}</td>
                  <td style={{ padding: 4 }}>{it.type}</td>
                  <td style={{ padding: 4 }}>{it.level}</td>
                  <td style={{ padding: 4 }}>{it.geom_type}</td>
                  <td><button style={{ ...btnGhost, padding: "2px 6px", fontSize: 10 }}
                    onClick={() => delItem(it.id)}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Riser viewer */}
        <div style={{ ...card, padding: 12, maxHeight: 320 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={label}>Riser Diagram (Ch 27.3)</div>
            <button data-testid="mepv2-load-riser" style={btn} onClick={loadRiser}>
              Generate {tab.toUpperCase()} Riser
            </button>
          </div>
          {riserSvg && <div style={{ marginTop: 8, maxHeight: 240, overflow: "auto" }}
                            dangerouslySetInnerHTML={{ __html: riserSvg }} />}
        </div>
      </div>

      {/* Clash report */}
      <div style={{ ...card, padding: 12, overflowY: "auto" }}>
        <div style={label}>Ceiling Coordination (Ch 27.2)</div>
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <div>Total clashes: <b style={{ color: clashes?.total ? "#ef4444" : "#22c55e" }}>
            {clashes?.total ?? "—"}</b></div>
          {clashes?.by_pair && Object.entries(clashes.by_pair).map(([k, v]) => (
            <div key={k} style={{ fontSize: 10, color: "var(--pf-muted)" }}>{k}: {v}</div>
          ))}
        </div>
        <table style={{ width: "100%", marginTop: 12, fontSize: 10 }}>
          <thead><tr><th style={{ textAlign: "left", color: "var(--pf-muted)" }}>Element 1</th>
            <th style={{ textAlign: "left", color: "var(--pf-muted)" }}>Element 2</th>
            <th style={{ color: "var(--pf-muted)" }}>Gap</th></tr></thead>
          <tbody>
            {(clashes?.clashes || []).slice(0, 30).map(c => (
              <tr key={c.id}>
                <td style={{ padding: 2 }}>{c.element_1}</td>
                <td style={{ padding: 2 }}>{c.element_2}</td>
                <td style={{ padding: 2, color: "#ef4444" }}>{c.gap_mm} mm</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {err && <div style={{ position: "absolute", bottom: 8, color: "#FF8888" }}>{err}</div>}
    </div>
  );
}
